import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { shouldShowConditionalDocument, isConditionalDocumentRequired } from '../rules/documentRules';
import { validatePDF, validateTotalSize, getVisibleConditionalDocs, calculateRequiredProgress } from '../services/documentService';
import { gerarResumoRequerimentoPDF, RESUMO_REQUERIMENTO_DOC_ID } from '../services/resumoRequerimentoService';
import { DOCUMENTO_ADICIONAL_PREFIX, isDocumentoAdicional } from '../services/documentoAnexoRequerimentoService';
import logger from '../../../../utils/logger';
import { destacarPendencias } from '../../../../utils/destaqueCampos';

// Descritor do documento gerado automaticamente (Resumo do Requerimento) —
// aplicado a TODOS os pedidos, independente do documentConfig de cada um.
const RESUMO_REQUERIMENTO_DESCRITOR = {
  id: RESUMO_REQUERIMENTO_DOC_ID,
  name: 'Resumo do Requerimento',
  description: 'Gerado automaticamente com os dados preenchidos neste pedido'
};

export const useDocuments = (config, formData, onComplete, { nomeRequerente, numeroPedido, comTaxa } = {}) => {
  const documentConfig = config?.documentConfig;
  const [documentos, setDocumentos] = useState({});
  // Erro de validação do arquivo (tipo/tamanho), por documento — exibido no
  // próprio item, perto de onde a pessoa acabou de anexar.
  const [uploadErros, setUploadErros] = useState({});
  const [tentouAvancar, setTentouAvancar] = useState(false);
  const [loading, setLoading] = useState(false);
  // Começa false (bloqueia "Continuar") até o efeito abaixo terminar de gerar
  // o PDF — evita que o pedido avance antes do resumo estar pronto.
  const [resumoGerado, setResumoGerado] = useState(false);
  const resumoSolicitadoRef = useRef(false);

  // Gera o PDF de resumo uma única vez por pedido aberto e injeta como
  // documento já "enviado" — a pessoa nunca faz upload dele, só vê o resultado
  // pronto (ver handleRemove abaixo, que recusa remover esse id).
  useEffect(() => {
    if (!config || resumoSolicitadoRef.current) return;
    resumoSolicitadoRef.current = true;

    (async () => {
      try {
        const file = await gerarResumoRequerimentoPDF({ config, formData, nomeRequerente, numeroPedido, comTaxa });
        setDocumentos(prev => ({ ...prev, [RESUMO_REQUERIMENTO_DOC_ID]: file }));
      } catch (err) {
        logger.warn('Não foi possível gerar o Resumo do Requerimento automaticamente:', err.message);
      } finally {
        setResumoGerado(true);
      }
    })();
  }, [config, formData, nomeRequerente, numeroPedido, comTaxa]);

  const requiredDocs = useMemo(() => documentConfig?.required || [], [documentConfig]);
  const conditionalDocs = useMemo(() => documentConfig?.conditional || [], [documentConfig]);
  const optionalDocs = useMemo(() => documentConfig?.optional || [], [documentConfig]);

  const visibleConditionalDocs = useMemo(() => {
    return getVisibleConditionalDocs(conditionalDocs, formData, shouldShowConditionalDocument);
  }, [conditionalDocs, formData]);

  const allRequiredDocs = useMemo(() => {
    const required = [...requiredDocs];
    
    visibleConditionalDocs.forEach(doc => {
      const isRequired = isConditionalDocumentRequired(doc, formData);
      if (isRequired) {
        required.push(doc);
      }
    });
    
    return required;
  }, [requiredDocs, visibleConditionalDocs, formData]);

  const progress = useMemo(() => {
    return calculateRequiredProgress(allRequiredDocs, documentos);
  }, [allRequiredDocs, documentos]);

  // Derivado: encolhe sozinho conforme a pessoa anexa os arquivos.
  const documentosFaltando = useMemo(
    () => allRequiredDocs.filter(doc => !documentos[doc.id]),
    [allRequiredDocs, documentos]
  );

  const definirUploadErro = useCallback((documentoId, erro) => {
    setUploadErros(prev => {
      const next = { ...prev };
      if (erro) next[documentoId] = erro;
      else delete next[documentoId];
      return next;
    });
  }, []);

  const handleUpload = useCallback((documentoId, file) => {
    const validation = validatePDF(file);
    if (!validation.isValid) {
      definirUploadErro(documentoId, validation.error);
      return false;
    }

    const outrosArquivos = Object.entries(documentos)
      .filter(([id]) => id !== documentoId)
      .map(([, arquivo]) => arquivo);
    const totalValidation = validateTotalSize([...outrosArquivos, file]);
    if (!totalValidation.isValid) {
      definirUploadErro(documentoId, totalValidation.error);
      return false;
    }

    setDocumentos(prev => ({ ...prev, [documentoId]: file }));
    definirUploadErro(documentoId, null);
    return true;
  }, [documentos, definirUploadErro]);

  // "Anexar mais documentos" (documentConfig.documentosAdicionais): quantos PDFs
  // a pessoa quiser, cada um com um id próprio no mesmo map de documentos — o
  // limite de tamanho total e o envio (tipo 281) valem pra eles também.
  const [erroAdicionais, setErroAdicionais] = useState(null);
  const adicionalSeqRef = useRef(0);

  const documentosAdicionais = useMemo(
    () => Object.entries(documentos).filter(([id]) => isDocumentoAdicional(id)).map(([id, file]) => ({ id, file })),
    [documentos]
  );

  const handleAdicionarAdicionais = useCallback((files) => {
    const validos = [];
    const recusados = [];
    files.forEach(file => {
      const validation = validatePDF(file);
      if (validation.isValid) validos.push(file);
      else recusados.push(`${file.name}: ${validation.error}`);
    });

    if (validos.length > 0) {
      const totalValidation = validateTotalSize([...Object.values(documentos), ...validos]);
      if (!totalValidation.isValid) {
        setErroAdicionais(totalValidation.error);
        return;
      }
      const novos = Object.fromEntries(validos.map(file => {
        adicionalSeqRef.current += 1;
        return [`${DOCUMENTO_ADICIONAL_PREFIX}${adicionalSeqRef.current}`, file];
      }));
      setDocumentos(prev => ({ ...prev, ...novos }));
    }

    setErroAdicionais(recusados.length > 0 ? `Arquivos recusados:\n${recusados.join('\n')}` : null);
  }, [documentos]);

  // Desmarcar "Anexar mais documentos" descarta os extras já escolhidos.
  const handleLimparAdicionais = useCallback(() => {
    setDocumentos(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => !isDocumentoAdicional(id))));
    setErroAdicionais(null);
  }, []);

  const handleRemove = useCallback((documentoId) => {
    if (documentoId === RESUMO_REQUERIMENTO_DOC_ID) return;
    setDocumentos(prev => {
      const newDocs = { ...prev };
      delete newDocs[documentoId];
      return newDocs;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!resumoGerado) return;

    if (documentosFaltando.length > 0) {
      setTentouAvancar(true);
      destacarPendencias(documentosFaltando.map(doc => doc.id));
      return;
    }

    setTentouAvancar(false);
    setLoading(true);
    try {
      await onComplete(documentos);
    } finally {
      setLoading(false);
    }
  }, [resumoGerado, documentosFaltando, documentos, onComplete]);

  // Remove documentos condicionais que não estão mais visíveis
  useEffect(() => {
    const visibleConditionalIds = visibleConditionalDocs.map(doc => doc.id);
    const currentDocIds = Object.keys(documentos);

    currentDocIds.forEach(docId => {
      const isConditional = conditionalDocs.some(doc => doc.id === docId);
      const isVisible = visibleConditionalIds.includes(docId);
      if (isConditional && !isVisible) {
        handleRemove(docId);
      }
    });
  }, [formData, visibleConditionalDocs, conditionalDocs, documentos, handleRemove]);

  // Auto-completa quando não há documentos obrigatórios pra pessoa enviar
  // manualmente — precisa esperar o resumo terminar de gerar (resumoGerado),
  // senão o requerimento avançaria sem o PDF automático anexado.
  useEffect(() => {
    const documentosManuais = Object.keys(documentos).filter(id => id !== RESUMO_REQUERIMENTO_DOC_ID);
    if (resumoGerado && progress.total === 0 && documentosManuais.length === 0) {
      const timer = setTimeout(() => onComplete(documentos), 500);
      return () => clearTimeout(timer);
    }
  }, [progress.total, documentos, onComplete, resumoGerado]);

  return {
    documentos,
    requiredDocs,
    visibleConditionalDocs,
    optionalDocs,
    progress,
    loading,
    documentosFaltando,
    uploadErros,
    tentouAvancar,
    setTentouAvancar,
    resumoDoc: RESUMO_REQUERIMENTO_DESCRITOR,
    resumoFile: documentos[RESUMO_REQUERIMENTO_DOC_ID],
    resumoGerado,
    permiteDocumentosAdicionais: Boolean(documentConfig?.documentosAdicionais),
    documentosAdicionais,
    erroAdicionais,
    handleAdicionarAdicionais,
    handleLimparAdicionais,
    handleUpload,
    handleRemove,
    handleSubmit
  };
};